import React, { FC, useMemo } from 'react';

import SanityBlockRenderer, {
  ISerializers,
  ITypographyBlockOverrides,
  PassthroughComponent,
} from 'components/sanity-block-renderer';
import { primitive } from 'styles/constants';

import {
  Body1,
  Body2,
  Caption,
  Headline1,
  Headline2,
  Headline3,
  Headline4,
  Headline5,
  Headline6,
  Normal,
  Subtitle1,
  Subtitle2,
} from './styles';
import { HorizontalPosition, ITypographyBlockProps } from './types';

/**
 * TypographyBlock is a simplified Sanity Content Block
 * It only allows Typography, no images, list or links
 */
const TypographyBlock: FC<React.PropsWithChildren<ITypographyBlockProps>> = ({
  content,
  color = primitive.$bbqBrown,
  horizontalPosition = HorizontalPosition.LEFT,
  ...props
}) => {
  const pointBlockContentOverrides = useMemo<ITypographyBlockOverrides>(
    () => ({
      headline1: ({ children }) => (
        <Headline1 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Headline1>
      ),
      headline2: ({ children }) => (
        <Headline2 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Headline2>
      ),
      headline3: ({ children }) => (
        <Headline3 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Headline3>
      ),
      headline4: ({ children }) => (
        <Headline4 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Headline4>
      ),
      headline5: ({ children }) => (
        <Headline5 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Headline5>
      ),
      headline6: ({ children }) => (
        <Headline6 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Headline6>
      ),
      subtitle1: ({ children }) => (
        <Subtitle1 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Subtitle1>
      ),
      subtitle2: ({ children }) => (
        <Subtitle2 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Subtitle2>
      ),
      normal: ({ children }) => (
        <Normal color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Normal>
      ),
      body1: ({ children }) => (
        <Body1 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Body1>
      ),
      body2: ({ children }) => (
        <Body2 color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Body2>
      ),
      caption: ({ children }) => (
        <Caption color={color} horizontalPosition={horizontalPosition} {...(props as any)}>
          {children}
        </Caption>
      ),
    }),
    [color, horizontalPosition, props]
  );

  const pointBlockSerializers: ISerializers = {
    marks: {},
    container: PassthroughComponent,
  };
  return (
    <SanityBlockRenderer
      blockContentOverrides={pointBlockContentOverrides}
      serializers={pointBlockSerializers}
      content={content}
    />
  );
};

export default TypographyBlock;
